import NiceModal, { useModal } from '@ebay/nice-modal-react'
import classNames from 'classnames'
import { useState } from 'react'
import { useRequest } from 'alova/client'
import { Trans, useTranslation } from 'react-i18next'
import AModal from '@/components/A/AModal/AModal'
import AIcon from '@/components/A/AIcon/AIcon'
import { commonUtils } from '@/helpers/commonUtils'
import styles from './ProductSwitchModal.module.less'
import { corp, type AnalyseCountCategoryCamel } from '@/api/corp/corp'
import { api } from '@/api'

type ProductSwitchTab = 'tvc' | 'portrait'
type ProductCardId =
  | 'tvc-quick'
  | 'tvc-base'
  | 'tvc-survey'
  | 'portrait-quick'
  | 'portrait-base'
  | 'portrait-survey'
type ProductCard = {
  actionText: string
  canEnter: boolean
  coreMetricsItems: string[]
  descriptionHighlight: string
  id: ProductCardId
  iconType: string
  isInfinite: boolean
  isComingSoon?: boolean
  outputResultItems: string[]
  productType: ProjectModels.ProjectCategory
  remainingTimes: number
  title: string
}

interface Props {
  entryData?: { versionInfoMap: Record<AnalyseCountCategoryCamel, corp.VersionInfoItem> }
  productType: ProjectModels.ProjectCategory
  onSelectProduct?: (productType: ProjectModels.ProjectCategory) => void
}

function ProductSwitchModal(props: Props) {
  const modal = useModal()
  const { t } = useTranslation()
  const { entryData } = props
  const { data: fetchedEntryData } = useRequest(api.corp.analysisCntEntry())
  const resolvedEntryData = fetchedEntryData ?? entryData
  const mapKeyByProductType: Record<ProjectModels.ProjectCategory, AnalyseCountCategoryCamel> = {
    'horizontal-quick': 'quickLandscape',
    'horizontal-base': 'baseLandscape',
    'horizontal-survey': 'surveyLandscape',
    'portrait-quick': 'quickPortrait',
    'portrait-base': 'basePortrait',
    'portrait-survey': 'surveyPortrait',
  }
  const getVersionInfo = (productType: ProjectModels.ProjectCategory) => {
    const mapKey = mapKeyByProductType[productType]
    return resolvedEntryData?.versionInfoMap?.[mapKey]
  }
  const [activeTab, setActiveTab] = useState<ProductSwitchTab>('tvc')
  const tvcProductCards: ProductCard[] = [
    {
      id: 'tvc-quick',
      productType: 'horizontal-quick',
      title: t('productSwitch.modal.cards.tvcQuick.title'),
      iconType: 'rocket',
      isInfinite: getVersionInfo('horizontal-quick')?.isInfinite ?? false,
      remainingTimes: getVersionInfo('horizontal-quick')?.corpAnalyseCount ?? 0,
      canEnter: getVersionInfo('horizontal-quick')?.canEnter ?? false,
      descriptionHighlight: t('productSwitch.modal.cards.tvcQuick.descriptionHighlight'),
      coreMetricsItems: [
        t('productSwitch.modal.cognitiveNeuroscienceIndicators'),
        t('productSwitch.modal.consumerAttitudeIndicators'),
      ],
      outputResultItems: [t('productSwitch.modal.dataReport')],
      actionText: t('productSwitch.modal.accessBackend'),
    },
    {
      id: 'tvc-base',
      productType: 'horizontal-base',
      title: t('productSwitch.modal.cards.tvcBase.title'),
      iconType: 'video',
      isInfinite: getVersionInfo('horizontal-base')?.isInfinite ?? false,
      remainingTimes: getVersionInfo('horizontal-base')?.corpAnalyseCount ?? 0,
      canEnter: getVersionInfo('horizontal-base')?.canEnter ?? false,
      descriptionHighlight: t('productSwitch.modal.cards.tvcBase.descriptionHighlight'),
      coreMetricsItems: [t('productSwitch.modal.cards.tvcBase.value2')],
      outputResultItems: [
        t('productSwitch.modal.dataReport'),
        t('productSwitch.modal.analysisReport'),
      ],
      actionText: t('productSwitch.modal.accessBackend'),
    },
    {
      id: 'tvc-survey',
      productType: 'horizontal-survey',
      title: t('productSwitch.modal.cards.tvcSurvey.title'),
      iconType: 'brain',
      isInfinite: getVersionInfo('horizontal-survey')?.isInfinite ?? false,
      remainingTimes: getVersionInfo('horizontal-survey')?.corpAnalyseCount ?? 0,
      canEnter: getVersionInfo('horizontal-survey')?.canEnter ?? false,
      descriptionHighlight: t('productSwitch.modal.cards.tvcSurvey.descriptionHighlight'),
      coreMetricsItems: [
        t('productSwitch.modal.cards.tvcSurvey.value2'),
        t('productSwitch.modal.consumerAttitudeIndicators'),
      ],
      outputResultItems: [
        t('productSwitch.modal.dataReport'),
        t('productSwitch.modal.analysisReport'),
      ],
      actionText: t('productSwitch.modal.accessBackend'),
    },
  ]
  const portraitProductCards: ProductCard[] = [
    {
      id: 'portrait-quick',
      productType: 'portrait-quick',
      title: t('productSwitch.modal.cards.portraitQuick.title'),
      iconType: 'rocket',
      isInfinite: getVersionInfo('portrait-quick')?.isInfinite ?? false,
      remainingTimes: getVersionInfo('portrait-quick')?.corpAnalyseCount ?? 0,
      canEnter: getVersionInfo('portrait-quick')?.canEnter ?? false,
      descriptionHighlight: t('productSwitch.modal.cards.portraitQuick.descriptionHighlight'),
      coreMetricsItems: [
        t('productSwitch.modal.cognitiveNeuroscienceIndicators'),
        t('productSwitch.modal.consumerAttitudeIndicators'),
      ],
      outputResultItems: [t('productSwitch.modal.dataReport')],
      actionText: t('productSwitch.modal.accessBackend'),
    },
    {
      id: 'portrait-base',
      productType: 'portrait-base',
      title: t('productSwitch.modal.cards.portraitBase.title'),
      iconType: 'video',
      isInfinite: getVersionInfo('portrait-base')?.isInfinite ?? false,
      remainingTimes: getVersionInfo('portrait-base')?.corpAnalyseCount ?? 0,
      canEnter: getVersionInfo('portrait-base')?.canEnter ?? false,
      descriptionHighlight: t('productSwitch.modal.cards.portraitBase.descriptionHighlight'),
      coreMetricsItems: [t('productSwitch.modal.cards.portraitBase.value2')],
      outputResultItems: [
        t('productSwitch.modal.dataReport'),
        t('productSwitch.modal.analysisReport'),
      ],
      actionText: t('productSwitch.modal.accessBackend'),
    },
    {
      id: 'portrait-survey',
      productType: 'portrait-survey',
      title: t('productSwitch.modal.cards.portraitSurvey.title'),
      iconType: 'brain',
      isInfinite: getVersionInfo('portrait-survey')?.isInfinite ?? false,
      remainingTimes: getVersionInfo('portrait-survey')?.corpAnalyseCount ?? 0,
      canEnter: true,
      descriptionHighlight: t('productSwitch.modal.cards.portraitSurvey.descriptionHighlight'),
      coreMetricsItems: [
        t('productSwitch.modal.cards.portraitSurvey.value2'),
        t('productSwitch.modal.consumerAttitudeIndicators'),
      ],
      outputResultItems: [
        t('productSwitch.modal.dataReport'),
        t('productSwitch.modal.analysisReport'),
      ],
      actionText: t('productSwitch.modal.accessBackend'),
    },
  ]
  const tabConfig: Record<
    ProductSwitchTab,
    {
      cards: ProductCard[]
      label: string
      supportedAssetsValue: string
      tagText?: string
      type: 'tvc' | 'portrait'
    }
  > = {
    tvc: {
      label: t('productSwitch.modal.tabs.tvc'),
      cards: tvcProductCards,
      supportedAssetsValue: t('productSwitch.modal.cards.tvcBase.value1'),
      type: 'tvc',
    },
    portrait: {
      label: t('productSwitch.modal.tabs.portrait'),
      cards: portraitProductCards,
      supportedAssetsValue: t('productSwitch.modal.cards.portraitBase.value1'),
      type: 'portrait',
    },
  }
  const activeTabConfig = tabConfig[activeTab]

  const handleProductSelect = (nextProductType: ProjectModels.ProjectCategory) => {
    modal.hide()
    props.onSelectProduct?.(nextProductType)
  }

  return (
    <AModal
      testId='product-switch-modal'
      className={styles.productSwitchModal}
      width='fit-content'
      open={modal.visible}
      onCancel={() => modal.hide()}
      footer={null}
      closeIcon={<AIcon type='icon-line-global_close1' width={16.2} height={16.2} />}
      afterClose={() => modal.remove()}
    >
      <div className={styles.productSwitchModalContent}>
        <div className={styles.heroSection}>
          <img
            className={styles.heroLogo}
            src={commonUtils.getImagePath('common/adeff-simple-logo.png')}
            alt=''
          />
          <div className={styles.heroCopy}>
            <h2 className={styles.heroTitle}>{t('productSwitch.modal.heroTitle')}</h2>
            <p className={styles.heroSubtitle}>{t('productSwitch.modal.heroSubtitle')}</p>
          </div>
        </div>

        <div className={styles.tabRow}>
          {Object.entries(tabConfig).map(([tabKey, currentTabConfig]) => {
            const tab = tabKey as ProductSwitchTab
            return (
              <button
                key={tab}
                type='button'
                data-testid={`product-switch-tab-${tab}`}
                className={classNames(styles.tabButton, {
                  [styles.active]: activeTab === tab,
                })}
                onClick={() => setActiveTab(tab)}
              >
                {currentTabConfig.label}
                {currentTabConfig.tagText && (
                  <span className={styles.comingSoonTag}>{currentTabConfig.tagText}</span>
                )}
              </button>
            )
          })}
        </div>

        <p className={styles.supportedAssets}>
          <span className={styles.supportedAssetsLabel}>
            {t('productSwitch.modal.supportedAssets')}:
          </span>{' '}
          {activeTabConfig.supportedAssetsValue}
        </p>

        <div className={styles.cardGridSection}>
          <div className={styles.cardGrid}>
            {activeTabConfig.cards.map((card) => {
              const iconType = card.isComingSoon ? `${card.iconType}-disabled` : card.iconType
              return (
                <section
                  key={card.id}
                  data-testid={`product-switch-card-${card.productType}`}
                  className={classNames(styles.productCard, {
                    [styles.comingSoon]: card.isComingSoon,
                  })}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.cardIconBox}>
                      <img
                        src={commonUtils.getImagePath(`productSwitchModal/${iconType}.png`)}
                        className={styles.cardIcon}
                        width={25.2}
                        height={25.2}
                      />
                    </div>
                    <div className={styles.cardHeaderCopy}>
                      <div className={styles.cardTitleRow}>
                        <h3 className={styles.cardTitle}>{card.title}</h3>
                        {card.isComingSoon && (
                          <span className={styles.cardComingSoonTag}>
                            {t('productSwitch.common.comingSoon')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className={styles.cardDescription}>
                    <span className={styles.cardDescriptionHighlight}>
                      {card.descriptionHighlight}
                    </span>
                  </p>

                  <div className={styles.cardInfo}>
                    <div className={styles.infoGroup}>
                      <h4 className={styles.infoTitle}>{t('productSwitch.modal.coreMetrics')}</h4>
                      <ul className={styles.infoList}>
                        {card.coreMetricsItems.map((item) => (
                          <li key={item} className={styles.infoItem}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={styles.infoGroup}>
                      <h4 className={styles.infoTitle}>{t('productSwitch.modal.outputResult')}</h4>
                      <ul className={styles.infoList}>
                        {card.outputResultItems.map((item) => (
                          <li key={item} className={styles.infoItem}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className={styles.cardFooter}>
                    {!card.isComingSoon ? (
                      <button
                        type='button'
                        data-testid={`product-switch-select-${card.productType}-btn`}
                        className={styles.cardAction}
                        onClick={() => {
                          handleProductSelect(card.productType)
                        }}
                      >
                        {card.actionText}
                      </button>
                    ) : null}
                    <span className={styles.remainingText}>
                      {card.isInfinite ? (
                        <>
                          <Trans
                            i18nKey='productSwitch.modal.remainingTimes'
                            values={{ count: '' }}
                          />
                          <AIcon
                            type='icon-fill-global_Unlimited2'
                            className={styles.remainingIcon}
                          />
                        </>
                      ) : (
                        t('productSwitch.modal.remainingTimes', { count: card.remainingTimes })
                      )}
                    </span>
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      </div>
    </AModal>
  )
}

export default NiceModal.create(ProductSwitchModal)
